import React from 'react';
import { colors, borderRadius, fontSize, fontWeight, transitions } from '../../constants/theme';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  fullWidth = false,
  icon,
  iconPosition = 'left',
  className = '',
  style,
  ...rest
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const hasError = !!error;

  const containerStyles: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    width: fullWidth ? '100%' : 'auto',
    minWidth: '250px'
  };

  const labelStyles: React.CSSProperties = {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: hasError ? colors.ui.error : colors.text.primary,
    transition: `color ${transitions.fast}`
  };

  const inputWrapperStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  };

  const inputStyles: React.CSSProperties = {
    width: '100%',
    padding: icon 
      ? (iconPosition === 'left' ? '10px 12px 10px 40px' : '10px 40px 10px 12px')
      : '10px 12px',
    fontSize: fontSize.base,
    color: colors.text.primary,
    backgroundColor: colors.ui.bg,
    border: `1.5px solid ${
      hasError 
        ? colors.ui.error 
        : isFocused 
          ? colors.accent.blue 
          : colors.ui.border
    }`,
    borderRadius: borderRadius.md,
    outline: 'none',
    transition: `all ${transitions.fast}`,
    ...(isFocused && {
      boxShadow: hasError
        ? `0 0 0 3px rgba(232, 92, 86, 0.1)`
        : `0 0 0 3px rgba(77, 143, 234, 0.1)`
    }),
    ...style
  };

  const iconStyles: React.CSSProperties = {
    position: 'absolute',
    [iconPosition]: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.text.secondary,
    pointerEvents: 'none',
    fontSize: '18px'
  };

  const messageStyles: React.CSSProperties = {
    fontSize: fontSize.xs,
    color: hasError ? colors.ui.error : colors.text.secondary,
    marginTop: '2px'
  };

  return (
    <div style={containerStyles}>
      {label && (
        <label style={labelStyles}>
          {label}
        </label>
      )}
      
      <div style={inputWrapperStyles}>
        {icon && (
          <span style={iconStyles}>
            {icon}
          </span>
        )}
        <input
          className={className}
          style={inputStyles}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          aria-invalid={hasError}
          aria-describedby={error ? 'error-message' : helperText ? 'helper-text' : undefined}
          {...rest}
        />
      </div>

      {(error || helperText) && (
        <span 
          id={error ? 'error-message' : 'helper-text'}
          style={messageStyles}
        >
          {error || helperText}
        </span>
      )}
    </div>
  );
};

export default Input;
export { Input };